// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t529 · B-2267 · LA CONSULTA GRABADA, EN LA PANTALLA (función «consulta-grabada», 28-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Lo que ve el doctor (el servidor: backend/services/consultaGrabada.js; la grabadora: utils/consultaGrabadaGrabadora.js):
//   · BotonGrabarConsulta (la cita y el Modo doctor): «Grabar la consulta», con una línea discreta para el doctor («Puedes
//     comentárselo al paciente de palabra»: ni bloquea ni pide un clic), el micrófono de este ordenador y lo ya grabado de
//     esa cita. Solo sale con la función encendida y a quien la clínica ha elegido (GET /consulta-grabada/puedo).
//   · IndicadorConsultaGrabada (montado una vez en App.jsx): mientras graba, el punto rojo y el tiempo, SIEMPRE a la vista
//     (también al cambiar de pantalla), con pausar, parar y descartar; después, «pasando a texto» y «lista para revisar»;
//     y «¿Grabar consulta? Sí / No» arriba en la esquina cuando la cita pasa a gabinete (el aviso del ordenador del gabinete,
//     EVENTO_EN_GABINETE, o al abrir la ficha de un paciente que HOY está en gabinete). Nada de esto lo ve el paciente.
//   · RevisarConsultaGrabada: el resumen muy breve en sus cinco apartados, que el doctor corrige; las propuestas de las
//     palabras clave, que solo se crean si las marca; la transcripción plegada (solo la ve él y se borra al guardar o a las
//     24 horas); y «Guardar el resumen en la cita». Nada se guarda ni se crea sin ese clic.
//   · SeccionConsultaGrabada (Ajustes, Citas y agenda): quién puede grabar la consulta y el micrófono de este ordenador.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import api from '../services/api'
import { useFuncion } from '../hooks/useFuncion'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { confirmar } from '../context/DialogosContext'
import { msgError } from '../utils/errores'
import { t, tx } from '../utils/i18n'
import {
  EVENTO_CAMBIO, suscribir, estadoGrabadora, grabandoAhora, microfonoGuardado, guardarMicrofono, listarMicrofonos, puedePausarAqui,
  empezar as empezarGrabacion, pausar as pausarGrabacion, reanudar as reanudarGrabacion, parar as pararGrabacion,
  descartar as descartarGrabacion, olvidarUltima,
} from '../utils/consultaGrabadaGrabadora'
// La grabadora (una por pestaña), con sus nombres de siempre.
const G = Object.freeze({ EVENTO_CAMBIO, suscribir, estadoGrabadora, grabandoAhora, microfonoGuardado, guardarMicrofono, listarMicrofonos, puedePausarAqui,
  empezar: empezarGrabacion, pausar: pausarGrabacion, reanudar: reanudarGrabacion, parar: pararGrabacion, descartar: descartarGrabacion, olvidarUltima })

// El aviso que lanza la pantalla del gabinete cuando un paciente pasa al gabinete de ESTE ordenador (t530): { citaId, pacienteId }.
export const EVENTO_EN_GABINETE = 'biango:paciente-en-gabinete'
export const EVENTO_REVISAR = 'biango:consulta-grabada-revisar'
export function abrirRevisionConsulta(id) {
  try { window.dispatchEvent(new CustomEvent(EVENTO_REVISAR, { detail: { id } })) } catch (_) { /* sin ventana */ }
}

const ROJO = '#dc2626'
const fmt = (s) => { const n = Math.max(0, Number(s) || 0); return String(Math.floor(n / 60)).padStart(2, '0') + ':' + String(n % 60).padStart(2, '0') }
const btn = (primario) => ({ padding: '7px 12px', borderRadius: '9px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', lineHeight: 1.2,
  border: '1px solid ' + (primario ? 'var(--primary)' : 'var(--border)'), background: primario ? 'var(--primary)' : 'var(--surface-2)',
  color: primario ? 'var(--sobre-primary)' : 'var(--text)' })

function useGrabadora() {
  const [s, setS] = useState(G.estadoGrabadora())
  useEffect(() => G.suscribir(setS), [])
  return s
}
// ¿Puedo grabar? Con la función encendida y si la clínica me ha elegido. Sin respuesta, no.
function usePuedoGrabar() {
  const on = useFuncion('consulta-grabada')
  const { user } = useAuth() || {}
  const { data } = useQuery({
    queryKey: ['consulta-grabada-puedo', user && user.id],
    queryFn: () => api.get('/consulta-grabada/puedo').then((r) => r.data).catch(() => ({ puede: false })),
    enabled: !!(on && user), staleTime: 60000,
  })
  return on && user && data && data.puede ? data : null
}
// Cuando la grabadora cambia (empieza, para, se descarta), lo que la escucha se vuelve a pedir.
function useAlCambiar(fn) {
  const ref = useRef(fn)
  ref.current = fn
  useEffect(() => {
    const h = () => { try { ref.current() } catch (_) { /* nada */ } }
    window.addEventListener(G.EVENTO_CAMBIO, h)
    return () => window.removeEventListener(G.EVENTO_CAMBIO, h)
  }, [])
}

function nombreEstado(e) {
  return ({
    grabando: t('components.ConsultaGrabada.estado_grabando', 'Grabando'),
    pendiente: t('components.ConsultaGrabada.estado_pendiente', 'Pasando a texto'),
    transcribiendo: t('components.ConsultaGrabada.estado_transcribiendo', 'Pasando a texto'),
    resumiendo: t('components.ConsultaGrabada.estado_resumiendo', 'Preparando el resumen'),
    lista: t('components.ConsultaGrabada.estado_lista', 'Lista para revisar'),
    guardando: t('components.ConsultaGrabada.estado_guardando', 'Guardando'),
    guardada: t('components.ConsultaGrabada.estado_guardada', 'Resumen guardado en la cita'),
    error: t('components.ConsultaGrabada.estado_error', 'No se ha podido pasar a texto (se vuelve a intentar solo)'),
    fallida: t('components.ConsultaGrabada.estado_fallida', 'No se ha podido pasar a texto: el audio se ha borrado'),
    descartada: t('components.ConsultaGrabada.estado_descartada', 'Descartada'),
    caducada: t('components.ConsultaGrabada.estado_caducada', 'No se guardó en 24 horas: se ha borrado'),
  })[e] || e
}

// ── EL MICRÓFONO DE ESTE ORDENADOR ─────────────────────────────────────────────────────────────────────────────
export function SelectorMicrofono() {
  const [lista, setLista] = useState(null)
  const [elegido, setElegido] = useState(() => G.microfonoGuardado())
  const [cargando, setCargando] = useState(false)
  const cargar = async (pedirPermiso) => {
    setCargando(true)
    try { setLista(await G.listarMicrofonos({ pedirPermiso })) } finally { setCargando(false) }
  }
  useEffect(() => { cargar(false) }, [])
  const elegir = (m) => { G.guardarMicrofono(m); setElegido(m ? { deviceId: m.deviceId, label: m.label } : null) }
  if (lista && !lista.soporta) return <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>{t('components.ConsultaGrabada.sin_elegir_micro', 'Este navegador no deja elegir el micrófono: se graba con el de siempre.')}</div>
  const opciones = (lista && lista.lista) || []
  return (
    <div data-selector-microfono="" style={{ display: 'grid', gap: '6px', fontSize: '13px' }}>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
        <input type="radio" name="cg-micro" checked={!elegido} onChange={() => elegir(null)} />
        {t('components.ConsultaGrabada.micro_de_siempre', 'El de siempre (el que tenga elegido el ordenador)')}
      </label>
      {opciones.filter((d) => d.deviceId && d.deviceId !== 'default').map((d, i) => (
        <label key={d.deviceId} style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
          <input type="radio" name="cg-micro" checked={!!elegido && elegido.deviceId === d.deviceId} onChange={() => elegir(d)} />
          {d.label || tx('components.ConsultaGrabada.micro_n', 'Micrófono {{n}}', { n: i + 1 })}
        </label>
      ))}
      {elegido && !opciones.some((d) => d.deviceId === elegido.deviceId) && lista && (
        <div style={{ fontSize: '12px', color: '#b45309' }}>{tx('components.ConsultaGrabada.micro_no_esta', 'El micrófono elegido ({{x}}) no está conectado ahora: se grabará con el de siempre hasta que vuelva.', { x: elegido.label || '?' })}</div>
      )}
      {lista && !lista.conNombres && (
        <button type="button" disabled={cargando} onClick={() => cargar(true)} style={{ ...btn(false), justifySelf: 'start' }}>
          {t('components.ConsultaGrabada.ver_nombres_micro', 'Ver los nombres de los micrófonos (pide permiso al micrófono)')}
        </button>
      )}
      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{t('components.ConsultaGrabada.micro_este_ordenador', 'Se guarda en este ordenador. Un micrófono Bluetooth tiene que estar conectado antes de grabar.')}</div>
    </div>
  )
}

// ── EL BOTÓN (la cita y el Modo doctor) ──────────────────────────────────────────────────────────────────────────
// `estilo`: el hueco alrededor, puesto aquí y no en quien lo pinta: con la función apagada (o sin poder grabar) no sale nada, ni el hueco.
export default function BotonGrabarConsulta({ citaId, estilo }) {
  const puedo = usePuedoGrabar()
  const g = useGrabadora()
  const toast = useToast()
  const qc = useQueryClient()
  const [verMicro, setVerMicro] = useState(false)
  const { data: lista = [], refetch } = useQuery({
    queryKey: ['consulta-grabada-cita', citaId],
    queryFn: () => api.get('/consulta-grabada/cita/' + citaId).then((r) => r.data).catch(() => []),
    enabled: !!(puedo && citaId),
    refetchInterval: (q) => ((q && q.state && Array.isArray(q.state.data) && q.state.data.some((x) => ['pendiente', 'transcribiendo', 'resumiendo', 'guardando'].includes(x.estado))) ? 5000 : false),
  })
  useAlCambiar(() => { refetch(); qc.invalidateQueries({ queryKey: ['consulta-grabada-abiertas'] }) })
  if (!puedo || !citaId) return null
  const estaCita = g.citaId != null && Number(g.citaId) === Number(citaId)
  const ocupada = G.grabandoAhora()
  const empezar = async () => {
    const r = await G.empezar({ citaId })
    if (!r.ok) toast.error(G.estadoGrabadora().error || t('components.ConsultaGrabada.no_empieza', 'No se ha podido empezar a grabar.'))
  }
  const micro = G.microfonoGuardado()
  return (
    <div data-consulta-grabada={citaId} style={{ display: 'grid', gap: '6px', ...(estilo || {}) }}>
      {ocupada && estaCita ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px', fontWeight: 700, color: ROJO }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: g.pausada ? '#9ca3af' : ROJO }} />
          {g.pausada ? t('components.ConsultaGrabada.en_pausa', 'En pausa') : t('components.ConsultaGrabada.grabando_esta', 'Grabando esta consulta')} · {fmt(g.segundos)}
          <button type="button" onClick={() => G.parar()} style={btn(true)}>{t('components.ConsultaGrabada.parar', 'Parar')}</button>
        </div>
      ) : (
        <button type="button" data-grabar-consulta="" disabled={ocupada} onClick={empezar}
          title={ocupada ? t('components.ConsultaGrabada.ya_grabando', 'Ya estás grabando otra consulta') : undefined}
          style={{ ...btn(true), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '9px 12px', background: ocupada ? 'var(--surface-2)' : ROJO, borderColor: ocupada ? 'var(--border)' : ROJO, color: ocupada ? 'var(--text-muted)' : '#fff', cursor: ocupada ? 'not-allowed' : 'pointer' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: ocupada ? '#9ca3af' : '#fff' }} />
          {ocupada ? t('components.ConsultaGrabada.ya_grabando', 'Ya estás grabando otra consulta') : t('components.ConsultaGrabada.grabar', 'Grabar la consulta')}
        </button>
      )}
      <div data-aviso-doctor="" style={{ fontSize: '11.5px', color: 'var(--text-muted)', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span>{puedo.aviso_doctor ? t('components.ConsultaGrabada.aviso_doctor', 'Puedes comentárselo al paciente de palabra.') : null}</span>
        <button type="button" onClick={() => setVerMicro((v) => !v)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', fontSize: '11.5px', fontWeight: 700 }}>
          {tx('components.ConsultaGrabada.micro_actual', 'Micrófono: {{x}}', { x: micro ? (micro.label || t('components.ConsultaGrabada.micro_elegido', 'el elegido')) : t('components.ConsultaGrabada.micro_siempre_corto', 'el de siempre') })}
        </button>
      </div>
      {verMicro && <div style={{ padding: '8px 10px', borderRadius: '9px', background: 'var(--surface-2)', border: '1px solid var(--border)' }}><SelectorMicrofono /></div>}
      {lista.filter((x) => !(ocupada && estaCita && x.estado === 'grabando')).length > 0 && (
        <div style={{ display: 'grid', gap: '4px' }}>
          {lista.filter((x) => !(ocupada && estaCita && x.estado === 'grabando')).slice(0, 5).map((x) => (
            <div key={x.id} data-consulta-de-la-cita={x.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
              <span>{x.fecha}{x.duracion_seg ? ' · ' + fmt(x.duracion_seg) : ''} · <b>{nombreEstado(x.estado)}</b></span>
              {['lista', 'error', 'pendiente', 'transcribiendo', 'resumiendo'].includes(x.estado) && (
                <button type="button" onClick={() => abrirRevisionConsulta(x.id)} style={{ ...btn(x.estado === 'lista'), padding: '4px 9px', fontSize: '12px' }}>
                  {x.estado === 'lista' ? t('components.ConsultaGrabada.revisar', 'Revisar y guardar') : t('components.ConsultaGrabada.ver', 'Ver')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── LA REVISIÓN ────────────────────────────────────────────────────────────────────────────────────────────────────
const TIPO_NOMBRE = () => ({
  cita: t('components.ConsultaGrabada.tipo_cita', 'Dar una cita (lo hace recepción)'),
  nota: t('components.ConsultaGrabada.tipo_nota', 'Nota en la ficha'),
  seguimiento: t('components.ConsultaGrabada.tipo_seguimiento', 'Seguimiento'),
  instruccion: t('components.ConsultaGrabada.tipo_instruccion', 'Recado para recepción'),
  medicacion: t('components.ConsultaGrabada.tipo_medicacion', 'Prescripción'),
  consentimiento: t('components.ConsultaGrabada.tipo_consentimiento', 'Consentimiento que preparar'),
  presupuesto: t('components.ConsultaGrabada.tipo_presupuesto', 'Presupuesto (lo prepara recepción)'),
  asistencia: t('components.ConsultaGrabada.tipo_asistencia', 'Asistencia a la cirugía'),
  laboratorio: t('components.ConsultaGrabada.tipo_laboratorio', 'Trabajo de laboratorio'),
  informe: t('components.ConsultaGrabada.tipo_informe', 'Informe de lo realizado'),
})
// Lo que se puede corregir de cada propuesta (lo mismo que deja el servidor: services/consultaGrabada · EDITABLE).
const CAMPOS = {
  cita: ['tratamiento', 'motivo', 'plazo_dias'], nota: ['texto'], seguimiento: ['titulo', 'plazo_dias'], instruccion: ['texto'],
  medicacion: ['nombre', 'dosis', 'pauta'], consentimiento: ['texto'], presupuesto: ['titulo', 'texto'], asistencia: ['titulo', 'texto'],
  laboratorio: ['texto'], informe: ['texto'],
}
const NOMBRE_CAMPO = () => ({
  tratamiento: t('components.ConsultaGrabada.campo_tratamiento', 'Tratamiento'), motivo: t('components.ConsultaGrabada.campo_motivo', 'Motivo'),
  plazo_dias: t('components.ConsultaGrabada.campo_plazo', 'En cuántos días'), texto: t('components.ConsultaGrabada.campo_texto', 'Texto'),
  titulo: t('components.ConsultaGrabada.campo_titulo', 'Título'), nombre: t('components.ConsultaGrabada.campo_nombre', 'Medicamento'),
  dosis: t('components.ConsultaGrabada.campo_dosis', 'Dosis'), pauta: t('components.ConsultaGrabada.campo_pauta', 'Pauta'),
})
const NOMBRE_SECCION = () => ({
  motivo: t('components.ConsultaGrabada.seccion_motivo', 'Motivo de la consulta'), visto: t('components.ConsultaGrabada.seccion_visto', 'Lo que se ha visto'),
  hecho: t('components.ConsultaGrabada.seccion_hecho', 'Lo que se ha hecho hoy'), plan: t('components.ConsultaGrabada.seccion_plan', 'Plan y próxima cita'),
  indicaciones: t('components.ConsultaGrabada.seccion_indicaciones', 'Indicaciones al paciente'),
})

export function RevisarConsultaGrabada({ id, onClose }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [secciones, setSecciones] = useState(null)
  const [acciones, setAcciones] = useState({})
  const [guardando, setGuardando] = useState(false)
  const { data: c, refetch, isLoading } = useQuery({
    queryKey: ['consulta-grabada', id],
    queryFn: () => api.get('/consulta-grabada/' + id).then((r) => r.data),
    enabled: !!id,
    refetchInterval: (q) => ((q && q.state && q.state.data && ['pendiente', 'transcribiendo', 'resumiendo', 'guardando'].includes(q.state.data.estado)) ? 3000 : false),
  })
  useEffect(() => {
    if (c && c.estado === 'lista' && secciones == null) {
      setSecciones(Object.fromEntries((c.secciones || []).map((s) => [s.clave, (c.resumen && c.resumen[s.clave]) || ''])))
      setAcciones(Object.fromEntries((c.propuestas || []).map((p) => [p.idx, { ...p, incluir: false }])))
    }
  }, [c, secciones])
  const cerrar = () => { qc.invalidateQueries({ queryKey: ['consulta-grabada-abiertas'] }); qc.invalidateQueries({ queryKey: ['consulta-grabada-cita'] }); onClose && onClose() }
  const guardar = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      const historia = { secciones, acciones: Object.values(acciones).map((a) => ({ ...a, idx: a.idx, incluir: a.incluir === true })) }
      const r = await api.post('/consulta-grabada/' + id + '/guardar', { historia })
      toast.success(r.data && r.data.acciones ? tx('components.ConsultaGrabada.guardado_con', 'Resumen guardado en la cita, con {{n}} cosas creadas', { n: r.data.acciones }) : t('components.ConsultaGrabada.guardado', 'Resumen guardado en la cita'))
      qc.invalidateQueries({ queryKey: ['notas-voz'] })
      qc.invalidateQueries({ queryKey: ['citas-hoy'] })
      cerrar()
    } catch (e) { toast.error(msgError(e, t('components.ConsultaGrabada.no_guardado', 'No se ha podido guardar'))) }
    setGuardando(false)
  }
  const descartar = async () => {
    const ok = await confirmar({ titulo: t('components.ConsultaGrabada.descartar_titulo', 'Descartar la consulta grabada'),
      mensaje: t('components.ConsultaGrabada.descartar_texto', 'Se borra todo lo de esta grabación (el texto y el resumen) y no se guarda nada en la cita.'), tipo: 'danger', textoConfirmar: t('components.ConsultaGrabada.descartar', 'Descartar') })
    if (!ok) return
    try { await api.post('/consulta-grabada/' + id + '/descartar'); toast.success(t('components.ConsultaGrabada.descartada', 'Descartada: no se ha guardado nada')); cerrar() }
    catch (e) { toast.error(msgError(e, t('components.ConsultaGrabada.no_descartada', 'No se ha podido descartar'))) }
  }
  const reintentar = async () => {
    try { await api.post('/consulta-grabada/' + id + '/reintentar'); setSecciones(null); refetch() }
    catch (e) { toast.error(msgError(e, t('components.ConsultaGrabada.no_reintenta', 'No se ha podido volver a intentar'))) }
  }
  const NOM = TIPO_NOMBRE()
  const CAMPO = NOMBRE_CAMPO()
  const SECCION = NOMBRE_SECCION()
  const campo = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '13.5px', fontFamily: 'inherit' }
  return (
    <div className="modal-safe" onClick={cerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
      <div data-revisar-consulta={id} onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: '14px', width: '100%', maxWidth: '720px', maxHeight: 'calc(92vh / var(--z, 1))', overflow: 'auto', padding: '16px 18px', display: 'grid', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: 800 }}>{t('components.ConsultaGrabada.titulo_revisar', 'Consulta grabada')}{c && c.paciente && c.paciente.nombre ? ' · ' + c.paciente.nombre : ''}</div>
            {c && <div style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{c.fecha}{c.duracion_seg ? ' · ' + fmt(c.duracion_seg) : ''} · {nombreEstado(c.estado)}</div>}
          </div>
          <button type="button" onClick={cerrar} style={{ ...btn(false), width: '34px', height: '34px', padding: 0, fontSize: '18px' }} aria-label={t('components.ConsultaGrabada.cerrar', 'Cerrar')}>×</button>
        </div>
        {isLoading && <div style={{ color: 'var(--text-muted)' }}>{t('components.ConsultaGrabada.cargando', 'Cargando…')}</div>}
        {c && ['pendiente', 'transcribiendo', 'resumiendo'].includes(c.estado) && (
          <div style={{ padding: '12px', borderRadius: '10px', background: 'var(--surface-2)', fontSize: '13.5px' }}>{t('components.ConsultaGrabada.trabajando', 'Pasando la consulta a texto y preparando el resumen. Puedes cerrar esta ventana: te avisará arriba cuando esté lista.')}</div>
        )}
        {c && ['error', 'fallida'].includes(c.estado) && (
          <div style={{ padding: '12px', borderRadius: '10px', background: '#fef2f2', color: '#991b1b', fontSize: '13.5px', display: 'grid', gap: '8px' }}>
            <span>{c.error || nombreEstado(c.estado)}</span>
            {c.estado === 'error' && <button type="button" onClick={reintentar} style={{ ...btn(true), justifySelf: 'start' }}>{t('components.ConsultaGrabada.reintentar', 'Volver a intentarlo ahora')}</button>}
          </div>
        )}
        {c && c.estado === 'guardada' && <div style={{ padding: '12px', borderRadius: '10px', background: '#ecfdf5', color: '#065f46', fontSize: '13.5px' }}>{tx('components.ConsultaGrabada.ya_guardada', 'El resumen se guardó en la cita el {{f}}.', { f: c.guardada_fecha || '' })}</div>}
        {c && c.estado === 'lista' && secciones && (
          <>
            {c.ejemplo_demo && <div style={{ padding: '10px 12px', borderRadius: '10px', background: '#fffbeb', color: '#92400e', fontSize: '13px' }}>{t('components.ConsultaGrabada.ejemplo_demo', 'Ejemplo de la demo: este servidor no ha podido pasar lo grabado a texto. En una clínica con la transcripción configurada, aquí sale lo de la consulta.')}</div>}
            {c.resumen_error && (
              <div style={{ padding: '10px 12px', borderRadius: '10px', background: '#fffbeb', color: '#92400e', fontSize: '13px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ flex: '1 1 240px' }}>{c.resumen_error}</span>
                {c.transcripcion && <button type="button" onClick={reintentar} style={btn(false)}>{t('components.ConsultaGrabada.reintentar_resumen', 'Volver a hacer el resumen')}</button>}
              </div>
            )}
            <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>{t('components.ConsultaGrabada.explica_resumen', 'El resumen documenta lo que se ha dicho: revísalo y corrígelo. Solo se guarda en la cita cuando pulses «Guardar el resumen en la cita».')}</div>
            <div style={{ display: 'grid', gap: '8px' }}>
              {(c.secciones || []).map((s) => (
                <label key={s.clave} style={{ display: 'grid', gap: '3px', fontSize: '12.5px', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  {SECCION[s.clave] || s.nombre}
                  <textarea rows={2} value={secciones[s.clave] || ''} maxLength={600} onChange={(e) => setSecciones((v) => ({ ...v, [s.clave]: e.target.value }))} style={{ ...campo, resize: 'vertical', fontWeight: 400 }} />
                </label>
              ))}
            </div>
            {Object.keys(acciones).length > 0 && (
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 800 }}>{t('components.ConsultaGrabada.propuestas', 'Propuestas de las palabras clave (solo se crea lo que marques)')}</div>
                {Object.values(acciones).map((a) => (
                  <div key={a.idx} data-propuesta={a.tipo} style={{ border: '1px solid ' + (a.incluir ? 'var(--primary)' : 'var(--border)'), borderRadius: '10px', padding: '9px 11px', display: 'grid', gap: '6px', opacity: a.sin_permiso ? 0.6 : 1 }}>
                    <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13.5px', fontWeight: 700, cursor: a.sin_permiso ? 'not-allowed' : 'pointer' }}>
                      <input type="checkbox" disabled={!!a.sin_permiso} checked={a.incluir === true} onChange={(e) => setAcciones((v) => ({ ...v, [a.idx]: { ...v[a.idx], incluir: e.target.checked } }))} />
                      {NOM[a.tipo] || a.tipo}
                      {a.palabra && <span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--text-muted)' }}>{tx('components.ConsultaGrabada.con_palabra', '(dicho con «{{p}}»)', { p: a.palabra })}</span>}
                    </label>
                    {a.sin_permiso && <div style={{ fontSize: '12px', color: '#991b1b' }}>{a.sin_permiso_texto}</div>}
                    {!a.sin_permiso && (CAMPOS[a.tipo] || []).map((k) => (
                      <label key={k} style={{ display: 'grid', gap: '2px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {CAMPO[k] || k}
                        <input type={k === 'plazo_dias' ? 'number' : 'text'} min={k === 'plazo_dias' ? 1 : undefined} value={a[k] == null ? '' : a[k]}
                          onChange={(e) => setAcciones((v) => ({ ...v, [a.idx]: { ...v[a.idx], [k]: e.target.value } }))} style={campo} />
                      </label>
                    ))}
                    {a.tipo === 'presupuesto' && Array.isArray(a.lineas) && a.lineas.length > 0 && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{a.lineas.map((l) => (l.cantidad > 1 ? l.cantidad + ' × ' : '') + (l.descripcion || l.dicho || '')).join(' · ')}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {c.transcripcion && (
              <details style={{ fontSize: '13px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700, color: 'var(--text-secondary)' }}>{tx('components.ConsultaGrabada.ver_transcripcion', 'Ver la transcripción (solo la ves tú; se borra al guardar el resumen o el {{f}})', { f: c.transcripcion_se_borra || '' })}</summary>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: '6px', padding: '10px', borderRadius: '9px', background: 'var(--surface-2)', maxHeight: '240px', overflow: 'auto', lineHeight: 1.5 }}>{c.transcripcion}</div>
              </details>
            )}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button type="button" onClick={descartar} style={{ ...btn(false), color: '#991b1b' }}>{t('components.ConsultaGrabada.descartar', 'Descartar')}</button>
              <button type="button" onClick={cerrar} style={btn(false)}>{t('components.ConsultaGrabada.luego', 'Revisarla luego')}</button>
              <button type="button" data-guardar-resumen="" disabled={guardando} onClick={guardar} style={btn(true)}>{guardando ? t('components.ConsultaGrabada.guardando', 'Guardando…') : t('components.ConsultaGrabada.guardar', 'Guardar el resumen en la cita')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── LO QUE SIEMPRE ESTÁ A LA VISTA (montado una vez en App.jsx) ──────────────────────────────────────────────────
export function IndicadorConsultaGrabada() {
  const puedo = usePuedoGrabar()
  const g = useGrabadora()
  const toast = useToast()
  const loc = useLocation()
  const [revisar, setRevisar] = useState(null)
  const [pregunta, setPregunta] = useState(null)   // { citaId, pacienteId }
  const [ultima, setUltima] = useState(null)       // { id, estado }
  const { data: abiertas = [], refetch: refetchAbiertas } = useQuery({
    queryKey: ['consulta-grabada-abiertas'],
    queryFn: () => api.get('/consulta-grabada/abiertas').then((r) => r.data).catch(() => []),
    enabled: !!puedo, staleTime: 30000, refetchInterval: 60000,
  })
  useAlCambiar(() => refetchAbiertas())
  // Abrir la revisión desde cualquier sitio.
  useEffect(() => {
    const h = (e) => { const id = e && e.detail && e.detail.id; if (id) setRevisar(Number(id)) }
    window.addEventListener(EVENTO_REVISAR, h)
    return () => window.removeEventListener(EVENTO_REVISAR, h)
  }, [])
  // Lo que acaba de parar: «pasando a texto» y, cuando esté, «lista para revisar».
  useEffect(() => {
    if (!g.ultima || !g.ultima.id) return undefined
    let vivo = true
    setUltima({ id: g.ultima.id, estado: 'pendiente' })
    const mirar = async () => {
      try {
        const r = await api.get('/consulta-grabada/' + g.ultima.id)
        if (!vivo) return
        setUltima({ id: g.ultima.id, estado: r.data.estado })
        if (['lista', 'error', 'fallida', 'descartada', 'guardada', 'caducada'].includes(r.data.estado)) { clearInterval(iv); refetchAbiertas() }
      } catch (_) { /* se vuelve a mirar */ }
    }
    const iv = setInterval(mirar, 4000)
    mirar()
    return () => { vivo = false; clearInterval(iv) }
  }, [g.ultima && g.ultima.id])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (g.error) toast.error(g.error, 9000) }, [g.error])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (g.aviso) toast.info(g.aviso, 9000) }, [g.aviso])   // eslint-disable-line react-hooks/exhaustive-deps

  // «¿GRABAR CONSULTA?»: al pasar a gabinete (el aviso del ordenador del gabinete) …
  const preguntada = (citaId) => { try { return sessionStorage.getItem('biango.consultaGrabada.preguntada.' + citaId) === '1' } catch (_) { return false } }
  const marcarPreguntada = (citaId) => { try { sessionStorage.setItem('biango.consultaGrabada.preguntada.' + citaId, '1') } catch (_) { /* nada */ } }
  useEffect(() => {
    if (!puedo) return undefined
    const h = (e) => {
      const d = (e && e.detail) || {}
      const citaId = Number(d.citaId || d.cita_id)
      if (!citaId || G.grabandoAhora() || preguntada(citaId)) return
      setPregunta({ citaId, pacienteId: Number(d.pacienteId || d.paciente_id) || null })
    }
    window.addEventListener(EVENTO_EN_GABINETE, h)
    return () => window.removeEventListener(EVENTO_EN_GABINETE, h)
  }, [puedo])
  // … o al abrir la ficha de un paciente que HOY está en gabinete.
  const pacienteDeLaFicha = useMemo(() => {
    // La ficha es /pacientes?paciente=N: la dirección entera, no un prefijo (una clínica puede tener una dirección propia que empiece igual).
    if (String(loc.pathname || '').replace(/\/+$/, '') !== '/pacientes') return null
    const p = Number(new URLSearchParams(loc.search || '').get('paciente'))
    return Number.isInteger(p) && p > 0 ? p : null
  }, [loc.pathname, loc.search])
  useEffect(() => {
    if (!puedo || !pacienteDeLaFicha || G.grabandoAhora()) return undefined
    let vivo = true
    api.get('/consulta-grabada/en-gabinete', { params: { paciente_id: pacienteDeLaFicha } }).then((r) => {
      const cita = r.data && r.data.cita
      if (!vivo || !cita || cita.ya_grabada || preguntada(cita.cita_id) || G.grabandoAhora()) return
      setPregunta({ citaId: cita.cita_id, pacienteId: pacienteDeLaFicha })
    }).catch(() => {})
    return () => { vivo = false }
  }, [puedo, pacienteDeLaFicha])

  if (!puedo && !G.grabandoAhora()) return null
  const sinTerminar = abiertas.filter((x) => x.estado === 'grabando' && x.quieta && !(g.consultaId && Number(g.consultaId) === Number(x.id)))
  const porRevisar = abiertas.filter((x) => x.estado === 'lista' && !(ultima && ultima.id === x.id && ultima.estado === 'lista'))
  const pill = { position: 'fixed', top: 'calc(10px + var(--sa-arriba, 0px))', right: '12px', zIndex: 2050, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    padding: '8px 10px', borderRadius: '14px', background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 6px 22px rgba(0,0,0,0.18)', maxWidth: 'calc((100vw / var(--z, 1)) - 24px)', fontSize: '13px' }
  const grabando = g.estado === 'grabando' || g.estado === 'parando' || g.estado === 'arrancando'
  return (
    <>
      {grabando && (
        <div data-indicador-consulta-grabada="" role="status" style={{ ...pill, borderColor: ROJO }}>
          <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: g.pausada ? '#9ca3af' : ROJO, animation: g.pausada ? 'none' : 'pulse 1s infinite' }} />
          <span data-reloj-consulta="" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: g.pausada ? 'var(--text-secondary)' : '#b91c1c' }}>{fmt(g.segundos)}</span>
          <span style={{ fontWeight: 700 }}>{g.estado === 'arrancando' ? t('components.ConsultaGrabada.arrancando', 'Preparando el micrófono…') : g.estado === 'parando' ? t('components.ConsultaGrabada.parando', 'Terminando…') : g.pausada ? t('components.ConsultaGrabada.en_pausa', 'En pausa') : t('components.ConsultaGrabada.grabando_consulta', 'Grabando la consulta')}{g.paciente ? ' · ' + g.paciente : ''}</span>
          {g.estado === 'grabando' && G.puedePausarAqui() && (g.pausada
            ? <button type="button" onClick={() => G.reanudar()} style={btn(false)}>{t('components.ConsultaGrabada.reanudar', 'Reanudar')}</button>
            : <button type="button" onClick={() => { if (!G.pausar()) toast.error(t('components.ConsultaGrabada.no_pausa', 'Este navegador no deja pausar: sigue grabando.')) }} style={btn(false)}>{t('components.ConsultaGrabada.pausar', 'Pausar')}</button>)}
          {g.estado === 'grabando' && <button type="button" data-parar-consulta="" onClick={() => G.parar()} style={btn(true)}>{t('components.ConsultaGrabada.parar', 'Parar')}</button>}
          {g.estado === 'grabando' && (
            <button type="button" onClick={async () => {
              const ok = await confirmar({ titulo: t('components.ConsultaGrabada.descartar_titulo', 'Descartar la consulta grabada'), mensaje: t('components.ConsultaGrabada.descartar_grabando', 'Se para la grabación y se borra lo grabado. No se guarda nada.'), tipo: 'danger', textoConfirmar: t('components.ConsultaGrabada.descartar', 'Descartar') })
              if (ok) G.descartar()
            }} style={{ ...btn(false), color: '#991b1b' }}>{t('components.ConsultaGrabada.descartar', 'Descartar')}</button>
          )}
          <span style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{tx('components.ConsultaGrabada.maximo', 'máx. {{m}} min', { m: Math.round((g.maxSegundos || 3600) / 60) })}</span>
        </div>
      )}
      {!grabando && pregunta && (
        <div data-pregunta-grabar="" style={pill}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: ROJO }} />
          <span style={{ fontWeight: 700 }}>{t('components.ConsultaGrabada.pregunta', '¿Grabar consulta?')}</span>
          <button type="button" data-pregunta-si="" onClick={async () => { const c = pregunta.citaId; marcarPreguntada(c); setPregunta(null); const r = await G.empezar({ citaId: c }); if (!r.ok) toast.error(G.estadoGrabadora().error || t('components.ConsultaGrabada.no_empieza', 'No se ha podido empezar a grabar.')) }} style={btn(true)}>{t('components.ConsultaGrabada.si', 'Sí')}</button>
          <button type="button" data-pregunta-no="" onClick={() => { marcarPreguntada(pregunta.citaId); setPregunta(null) }} style={btn(false)}>{t('components.ConsultaGrabada.no', 'No')}</button>
        </div>
      )}
      {!grabando && !pregunta && ultima && ['pendiente', 'transcribiendo', 'resumiendo', 'lista', 'error'].includes(ultima.estado) && (
        <div data-ultima-consulta="" style={pill}>
          <span>{ultima.estado === 'lista' ? t('components.ConsultaGrabada.lista_revisar', 'La consulta grabada está lista para revisar') : ultima.estado === 'error' ? t('components.ConsultaGrabada.no_texto_aun', 'No se ha podido pasar a texto todavía: se vuelve a intentar solo') : t('components.ConsultaGrabada.pasando', 'Pasando la consulta a texto…')}</span>
          {['lista', 'error'].includes(ultima.estado) && <button type="button" onClick={() => setRevisar(ultima.id)} style={btn(ultima.estado === 'lista')}>{ultima.estado === 'lista' ? t('components.ConsultaGrabada.revisar', 'Revisar y guardar') : t('components.ConsultaGrabada.ver', 'Ver')}</button>}
          <button type="button" onClick={() => { setUltima(null); G.olvidarUltima() }} style={{ ...btn(false), padding: '4px 9px' }} aria-label={t('components.ConsultaGrabada.cerrar', 'Cerrar')}>×</button>
        </div>
      )}
      {!grabando && !pregunta && !ultima && (sinTerminar.length > 0 || porRevisar.length > 0) && (
        <div data-consultas-pendientes="" style={{ ...pill, top: 'auto', bottom: 'calc(14px + var(--sa-abajo, 0px))' }}>
          {sinTerminar.length > 0 && (
            <>
              <span>{t('components.ConsultaGrabada.sin_terminar', 'Hay una grabación que no se paró')}</span>
              <button type="button" onClick={async () => { try { await api.post('/consulta-grabada/' + sinTerminar[0].id + '/parar', {}); refetchAbiertas() } catch (e) { toast.error(msgError(e, t('components.ConsultaGrabada.no_se_ha_podido_terminar', 'No se ha podido terminar'))) } }} style={btn(true)}>{t('components.ConsultaGrabada.terminar', 'Terminar y pasar a texto')}</button>
              <button type="button" onClick={async () => { try { await api.post('/consulta-grabada/' + sinTerminar[0].id + '/descartar'); refetchAbiertas() } catch (e) { toast.error(msgError(e, t('components.ConsultaGrabada.no_descartada', 'No se ha podido descartar'))) } }} style={btn(false)}>{t('components.ConsultaGrabada.descartar', 'Descartar')}</button>
            </>
          )}
          {sinTerminar.length === 0 && porRevisar.length > 0 && (
            <>
              <span>{porRevisar.length === 1 ? t('components.ConsultaGrabada.una_por_revisar', 'Tienes una consulta grabada por revisar') : tx('components.ConsultaGrabada.n_por_revisar', 'Tienes {{n}} consultas grabadas por revisar', { n: porRevisar.length })}</span>
              <button type="button" onClick={() => setRevisar(porRevisar[0].id)} style={btn(true)}>{t('components.ConsultaGrabada.revisar', 'Revisar y guardar')}</button>
            </>
          )}
        </div>
      )}
      {revisar && <RevisarConsultaGrabada id={revisar} onClose={() => { setRevisar(null); if (ultima && ultima.id === revisar) setUltima(null) }} />}
    </>
  )
}

// ── AJUSTES, CITAS Y AGENDA: QUIÉN PUEDE GRABAR LA CONSULTA ─────────────────────────────────────────────────────
// `Seccion` llega de pages/Ajustes (su caja plegable), para no importar Ajustes desde aquí.
export function SeccionConsultaGrabada({ Seccion }) {
  const on = useFuncion('consulta-grabada')
  if (!on || !Seccion) return null
  return (
    <Seccion icon={null} title={t('components.ConsultaGrabada.ajustes_titulo', 'Quién puede grabar la consulta')}
      subtitle={t('components.ConsultaGrabada.ajustes_sub', 'Las personas que ven «Grabar la consulta» en la cita y en el Modo doctor, y el micrófono de este ordenador')}>
      <AjustesConsultaGrabada />
    </Seccion>
  )
}
function AjustesConsultaGrabada() {
  const toast = useToast()
  const qc = useQueryClient()
  const { data, error, refetch } = useQuery({ queryKey: ['consulta-grabada-ajustes'], queryFn: () => api.get('/consulta-grabada/ajustes').then((r) => r.data) })
  const [marcados, setMarcados] = useState(null)
  useEffect(() => { if (data && marcados == null) setMarcados(new Set(data.elegidos || [])) }, [data, marcados])
  if (error) return <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{msgError(error, t('components.ConsultaGrabada.ajustes_solo_admin', 'Esto lo decide la administración de la clínica.'))}</div>
  if (!data || !marcados) return <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('components.ConsultaGrabada.cargando', 'Cargando…')}</div>
  const guardar = async () => {
    try { await api.put('/consulta-grabada/ajustes', { elegidos: [...marcados] }); toast.success(t('components.ConsultaGrabada.ajustes_guardados', 'Guardado')); refetch(); qc.invalidateQueries({ queryKey: ['consulta-grabada-puedo'] }) }
    catch (e) { toast.error(msgError(e, t('components.ConsultaGrabada.no_guardado', 'No se ha podido guardar'))) }
  }
  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {t('components.ConsultaGrabada.ajustes_explica', 'Quien esté marcado puede grabar la consulta entera: se pasa a texto con el mismo servicio que las notas de voz y la IA prepara un resumen muy breve. Solo se guarda el resumen, en la cita, cuando esa persona lo revisa y lo guarda; el audio se borra al pasarlo a texto y la transcripción al guardar (como mucho, a las 24 horas).')}
      </div>
      <div style={{ display: 'grid', gap: '4px' }}>
        {(data.equipo || []).map((p) => (
          <label key={p.id} data-elegible={p.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13.5px', cursor: 'pointer' }}>
            <input type="checkbox" checked={marcados.has(p.id)} onChange={(e) => setMarcados((s) => { const n = new Set(s); if (e.target.checked) n.add(p.id); else n.delete(p.id); return n })} />
            {p.nombre}
          </label>
        ))}
      </div>
      <button type="button" onClick={guardar} style={{ ...btn(true), justifySelf: 'start' }}>{t('components.ConsultaGrabada.ajustes_guardar', 'Guardar quién puede grabar')}</button>
      <div style={{ display: 'grid', gap: '6px' }}>
        <div style={{ fontSize: '13.5px', fontWeight: 800 }}>{t('components.ConsultaGrabada.micro_titulo', 'El micrófono de este ordenador')}</div>
        <SelectorMicrofono />
      </div>
    </div>
  )
}
